/**
 * Tagalys search-results controller.
 *
 * Drives the /search results page from the Tagalys `SearchResults` connector: grid,
 * facet rail, sort, result count and pagination all render from one JSON response
 * instead of the Section Rendering API round trip the native page used.
 *
 * WHY THIS IS A SEPARATE FILE FROM assets/facets.js
 * -------------------------------------------------
 * `assets/facets.js` is loaded by BOTH sections/main-search.liquid and
 * sections/main-collection-product-grid.liquid. The collection PLP stays
 * server-rendered Liquid on native Shopify filtering (explicitly out of scope), so
 * rewriting facets.js in place would take the PLP's filtering down with it. This file
 * is loaded ONLY by the search template; facets.js is left byte-identical, which makes
 * the PLP-regression check trivial. The cost is some duplicated toolbar wiring, which
 * is preferred over one class trying to serve two different state models.
 *
 * Dependencies, all already on the page:
 *   - window.onTagalysReady  (snippets/tagalys-bootstrap.liquid) - SDK ready queue
 *   - window.renderTagalysProductCards / tagalysCardAfterRender (tagalys-product-card.js)
 *   - window.tagalysCardStrings - translated strings + settings
 *
 * The connector re-invokes `onSuccess` for EVERY refinement (filter, sort, page), so
 * `render` is the single, idempotent paint path for both first load and every update.
 */
(function () {
	"use strict";

	/* Two mounts: the search template ([data-tagalys-search-results]) and, since
	   Productive #304, the collection grid ([data-tagalys-collection], carrying the
	   Shopify collection id). Bail on any other template so a stray asset include
	   cannot touch a page it does not own. */
	const root = document.querySelector(
		"[data-tagalys-search-results], [data-tagalys-collection]",
	);
	if (!root) return;

	const strings = window.tagalysCardStrings || {};
	const searchStrings = strings.search || {};
	const a11yStrings = strings.accessibility || {};
	const cardSettings = strings.settings || {};
	const loadMoreStrings = strings.loadMore || {};

	const PER_PAGE = parseInt(root.dataset.perPage, 10) || 24;

	/* ------------------------------------------------ collection mode (#304)
	   The PLP runs the `Collection` connector instead of `SearchResults`. Two things
	   differ from search, both driven by the flags below:

	   - NATIVE-FIRST GRID. The section ships its Liquid grid (Tagalys already orders
	     it server-side), so the first response only paints the rail, sort options and
	     count - the grid is NOT repainted, which would be a visible flash and would
	     throw away the crawlable first page. The first refinement (filter, sort, page,
	     clear) hands the grid over to this renderer (`takeOver`), after which every
	     response paints it, callout tiles included. Arriving on a URL that already
	     carries Tagalys state (`?filter=` / `?sort=` / `?page=`) takes over before the
	     first call.
	   - LOAD MORE. The PLP paginates by append (settings.pagination_type load_more,
	     snippets/load-more.liquid), so in that mode the controller renders the same
	     control - count, page row, Load More - and appends the next page's cards
	     instead of replacing them. Numbered pages remain for the other modes.

	   Failover: if Tagalys refuses the collection call (the 'collections' feature is
	   account-gated - `feature_not_enabled`), the Liquid page stands untouched. The
	   section ships Shopify's own filters and sort options in the toolbar
	   (keep_native_filters / keep_native_sort), so the controls stay on screen and
	   apply through a native reload with Shopify's `filter.*` / `sort_by` params -
	   exactly what facets.js would have submitted. Native vs Tagalys-rendered
	   controls are told apart by MARKUP (Tagalys inputs carry data-filter-id / a
	   price-range[data-filter-id]; Liquid inputs carry Shopify `name`s), not by a
	   failure flag, so a click that lands before the first response - or after a
	   refusal - always goes the native way and never serialises Tagalys ids into
	   a Liquid URL. `tagalysReady` (first successful response) is the one state
	   bit: until then the sort list and clear-all are Liquid's. */
	const collectionId = root.dataset.tagalysCollection || "";
	const isCollection = collectionId !== "";
	const usesLoadMore =
		isCollection &&
		/^(load_more|infinite_scroll)$/.test(root.dataset.paginationType || "");
	let nativeGrid = isCollection;
	let tagalysReady = false;
	let appendNext = false;
	/* Callout Card tiles from the Liquid grid, keyed by how many product cards precede
	   each one (0, 7, 15, 23 for the 1 / 8 / 16 / 24 loop-index placements), so the
	   client paint puts them back in the same slots on page 1. Cloned once at init. */
	const tileSlots = [];

	function captureTiles() {
		const grid = root.querySelector("[data-tagalys-grid]");
		if (!grid) return;
		let productIndex = 0;
		Array.from(grid.children).forEach((item) => {
			if (item.classList.contains("collection-product-promo-img")) {
				tileSlots.push({ before: productIndex, node: item.cloneNode(true) });
			} else {
				productIndex += 1;
			}
		});
	}

	/* Every non-append refinement: hand the grid over (once) and drop a pending
	   Load More append, so a filter/sort/page response that overtakes an in-flight
	   Load More can never be appended under the wrong grid. */
	function refine() {
		appendNext = false;
		takeOver();
		setBusy(true);
	}

	function takeOver() {
		if (!nativeGrid) return;
		nativeGrid = false;
		const native = root.querySelector("[data-tagalys-native-pagination]");
		if (native) native.hidden = true;
		const holder = root.querySelector("[data-tagalys-pagination]");
		if (holder) holder.hidden = false;
	}

	/* ---------------------------------------------------------------- utilities */

	/* The query lives in the URL. The SDK's own query-string configuration maps
	   `q` -> query, but the initial read has to happen before the connector exists. */
	function currentQuery() {
		const params = new URLSearchParams(window.location.search);
		return (params.get("q") || "").trim();
	}

	/* Mirrors Liquid's `handleize` closely enough for DOM ids. */
	function slugify(value) {
		return String(value == null ? "" : value)
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "");
	}

	function setBusy(isBusy) {
		/* Native-first PLP: the Liquid grid is on screen and the background call only
		   feeds the toolbar, so no overlay / aria-busy on the grid. */
		if (isCollection && nativeGrid) return;
		root.classList.toggle("loading", isBusy);
		const grid = root.querySelector("[data-tagalys-grid]");
		if (grid) grid.setAttribute("aria-busy", isBusy ? "true" : "false");
		if (isCollection) {
			/* Same hook facets.js toggles natively - the .collection wrapper owns the
			   loading overlay (template-collection.css). */
			const wrapper = root.querySelector("#ProductGridContainer .collection");
			if (wrapper) wrapper.classList.toggle("loading", isBusy);
		}
	}

	/* Both count slots the toolbar renders: the desktop filter button's "(N)" and the
	   filter modal heading's. Native markup keeps them as two ids, so mirror both. */
	function renderCount(total) {
		const value = typeof total === "number" ? total : 0;
		root.querySelectorAll("[data-tagalys-count]").forEach((el) => {
			el.textContent = value;
			el.classList.remove("loading");
		});
		/* The heading is in the page HERO, which is a PRECEDING SIBLING of this
		   controller's root - not a descendant - so it must be resolved from the
		   document. Scoping it to `root` silently found nothing and left the
		   server-rendered seed (Shopify's own count for the query) on screen. */
		const heading = document.querySelector("[data-tagalys-results-heading]");
		const headings = searchStrings.resultsHeading;
		if (heading && headings) {
			/* Plural pair, interpolated with the same [count] / [terms] placeholders the
			   string bundle emits (see layout/theme.liquid). */
			const template = value === 1 ? headings.one : headings.other;
			if (template) {
				heading.textContent = template
					.replace("[count]", value)
					.replace("[terms]", currentQuery());
			}
			/* The Liquid seed paints a pulsing placeholder in the count slot with
			   aria-busy on the heading (post-merge QA part 2); textContent above has
			   just replaced the placeholder, so the heading is no longer busy. */
			heading.removeAttribute("aria-busy");
		}
		/* Tagalys QA: the browser tab title and the breadcrumb both print
		   page_title ('Search: N results found for "terms"'), where N is
		   Shopify's own count for the query - it disagrees with the Tagalys
		   total rendered above (719 vs 135 for "boots" at review). Both are
		   synced by swapping the first numeric token in the seeded string, so
		   Shopify's title format (and any suffix) survives untouched. The
		   breadcrumb hook is data-tagalys-page-title (breadcrumbs.liquid). */
		/* Post-merge QA part 2: the seed is now an ellipsis where the count sits
		   (theme.liquid / breadcrumbs.liquid), so the token matches that too. */
		/* Search only: a collection's title carries no result count (and may carry a
		   number of its own, "Top 10 Boots", that must not be rewritten). */
		if (isCollection) return;
		const countToken = /\d[\d,.]*|\u2026/;
		if (countToken.test(document.title)) {
			document.title = document.title.replace(countToken, value);
		}
		const crumb = document.querySelector("[data-tagalys-page-title]");
		if (crumb && countToken.test(crumb.textContent)) {
			crumb.textContent = crumb.textContent.replace(countToken, value);
		}
	}

	/* True no-results hero (post-merge QA): the heading becomes
	   'No Results for “{terms}”' and the subcopy under it is unhidden. Must run
	   AFTER renderCount, which writes the '0 Results…' count heading this
	   overwrites. Both nodes live in the page hero — a preceding sibling of
	   root — so they resolve from the document, same as renderCount's heading.
	   The page-level .template-search--empty is re-toggled from the Tagalys
	   verdict because the Liquid seed uses Shopify's own count, which can
	   disagree; that class carries the whole no-results hero treatment
	   (left-align, border, subcopy type) in template-search.css. */
	function renderNoResultsHero(noResults) {
		const heading = document.querySelector("[data-tagalys-results-heading]");
		if (heading && noResults && searchStrings.noResultsHeading) {
			heading.textContent = searchStrings.noResultsHeading.replace(
				"[terms]",
				currentQuery(),
			);
		}
		const copy = document.querySelector("[data-tagalys-no-results-copy]");
		if (copy) copy.hidden = !noResults;
		const page = root.closest(".template-search");
		if (page) page.classList.toggle("template-search--empty", noResults);
	}

	/* Tagalys QA: query-verdict headings. When Tagalys rescued the query, say so
	   instead of presenting the grid as an exact match - a spell-corrected query
	   ("shrits" -> "shirts") reads 'No results for "x". Showing results for "y"
	   instead', a partial match reads 'No products exactly match "x". Showing
	   products that partially match' (strings: en.default.json templates.search,
	   bundled in theme.liquid). Runs AFTER renderCount/renderNoResultsHero so the
	   verdict overwrites the count heading, and skips the zero state - the
	   no-results hero owns that. Helper support is feature-tested because the
	   Shopify failover connector's helpers do not implement the verdict methods. */
	function renderQueryVerdict(helpers, noResults) {
		if (noResults) return;
		const heading = document.querySelector("[data-tagalys-results-heading]");
		if (!heading) return;
		if (
			typeof helpers.isSpellingCorrectedQuery === "function" &&
			helpers.isSpellingCorrectedQuery() &&
			searchStrings.spellCorrectedHeading
		) {
			const corrected =
				typeof helpers.getQuery === "function"
					? helpers.getQuery()
					: currentQuery();
			const original =
				typeof helpers.getOriginalQuery === "function"
					? helpers.getOriginalQuery()
					: currentQuery();
			heading.textContent = searchStrings.spellCorrectedHeading
				.replace("[original]", original)
				.replace("[corrected]", corrected);
			return;
		}
		if (
			typeof helpers.isPartialResults === "function" &&
			helpers.isPartialResults() &&
			searchStrings.partialResultsHeading
		) {
			heading.textContent = searchStrings.partialResultsHeading.replace(
				"[terms]",
				currentQuery(),
			);
		}
	}

	/* --------------------------------------------------------------------- grid */

	/* Paint the grid.

	   `renderTagalysProductCards` returns a PROMISE of DOM elements (it awaits the
	   Storefront collection enrichment internally so collection-driven badges resolve on
	   the first paint), and each element is a bare `.card-wrapper` div. The <li> wrapper
	   that snippets/product-card.liquid's caller supplies is therefore added here.

	   A render token guards against out-of-order responses: fast successive refinements
	   can resolve in any order, and a stale promise must not overwrite a newer grid. */
	let renderToken = 0;

	function renderGrid(products, page) {
		const grid = root.querySelector("[data-tagalys-grid]");
		if (!grid) return;

		const token = ++renderToken;
		/* #304 Load More: append this page's cards under the ones on screen. Consumed
		   here so a replace-mode response that overtakes it cannot inherit the flag. */
		const append = appendNext;
		appendNext = false;
		const pageIndex = Math.max(1, Number(page) || 1);

		if (!products || !products.length) {
			if (!append) grid.innerHTML = "";
			return;
		}

		window
			.renderTagalysProductCards(products)
			.then(function (cards) {
				if (token !== renderToken) return;

				if (!append) grid.innerHTML = "";
				const fragment = document.createDocumentFragment();
				cards.forEach(function (card, index) {
					if (isCollection && !append && pageIndex === 1) {
						/* Callout Card tiles go back before the product they preceded in the
						   Liquid grid - page 1 of a fresh paint only, as load-more.js does. */
						tileSlots.forEach(function (slot) {
							if (slot.before === index) fragment.appendChild(slot.node.cloneNode(true));
						});
					}
					const item = document.createElement("li");
					item.className =
						(isCollection ? "" : "template-search__item ") +
						"collection-product-card" +
						(cardSettings.onHover === "hover" ? " quickview--hover" : "");
					if (isCollection) {
						/* QA #219 mobile reading order - the same formula the Liquid writes:
						   products take the even order of their six-product group, offset by
						   the page cursor so appended pages sort after page 1. */
						const absolute = (pageIndex - 1) * PER_PAGE + index;
						item.style.setProperty("--plp-order", String(Math.floor(absolute / 6) * 2));
					}
					item.appendChild(card);
					fragment.appendChild(item);
				});
				grid.appendChild(fragment);

				/* Sliders, wishlist hearts, quick-add. Calls the GUARDED slider initialiser
				   in product-card-slider.js - never the unguarded copy that used to live in
				   facets.js. */
				if (typeof window.tagalysCardAfterRender === "function") {
					window.tagalysCardAfterRender(grid);
				}
				/* PLP toolbar state (grid size, Model / Product toggle) re-applies to new
				   cards on this event - the contract load-more.js established (#258). */
				if (isCollection) {
					document.dispatchEvent(
						new CustomEvent("plp:products-appended", {
							detail: { count: cards.length },
						}),
					);
				}
			})
			.catch(function () {
				/* Enrichment or a single card failing must not leave the grid spinning. */
				if (token === renderToken) setBusy(false);
			});
	}

	/* Empty state. The native page gated the whole toolbar+grid block on
	   `results_count > 0`, so a zero-result refinement re-rendered without
	   #ProductGridContainer and the grid stalled in its loading state forever. Here the
	   toolbar stays mounted and only the grid region swaps, so the shopper can always
	   relax the filter that stranded them. */
	function renderEmptyState(hasFilters, isEmpty) {
		const empty = root.querySelector("[data-tagalys-empty]");
		if (!empty) return;

		/* Only shown when there is genuinely nothing to show. This block is a SIBLING of
		   the grid (so the facet rail stays usable on a zero-result refinement) rather
		   than a replacement for it, which means its visibility has to be driven
		   explicitly - otherwise the "couldn't find what you were looking for" copy sat
		   under a full 24-card grid. */
		empty.hidden = !isEmpty;
		if (!isEmpty) return;

		/* Filtered to zero vs. the query itself missing are different messages: the
		   first is recoverable by relaxing a filter, the second by re-querying. */
		/* On the PLP there is no query to have missed - every zero is a filter zero. */
		const message =
			hasFilters || isCollection
				? searchStrings.emptyResults
				: searchStrings.noResultsBody;
		const target = empty.querySelector("[data-tagalys-empty-message]");
		if (target && message) target.textContent = message;
	}

	/* ------------------------------------------------------------------- facets */

	/* Tagalys returns `checkbox` and `range` filter types. Map them onto the existing
	   facet markup so component-facets.css applies unchanged.

	   NOTE: this store currently returns `filters: []` for every search query (Tagalys
	   has no search facets configured), so this path is inert in production today. It is
	   built out regardless: the rail populates the moment facets are switched on, with no
	   further code change. `renderFilterTrigger` hides the Filter control while empty so
	   the toolbar never offers a control that opens nothing. */
	function renderFacets(filters) {
		const wrapper = root.querySelector("[data-tagalys-facets]");
		if (!wrapper) return;

		wrapper.innerHTML = "";
		(filters || []).forEach((filter, index) => {
			const node =
				filter.type === "range"
					? buildRangeFacet(filter, index)
					: buildCheckboxFacet(filter, index);
			if (node) wrapper.appendChild(node);
		});

		renderFilterTrigger(filters);
		renderClearAll(filters);
	}

	/* `readout` is the selected-state secondary label the design-system Accordion Item
	   (Figma 10022:85068) carries after the heading - "(1)" for a list filter, the applied
	   bounds "($0 - $280)" for a range. Mirrors the span facets.liquid renders natively. */
	function buildDisclosure(filter, index, extraClass, readout) {
		const details = document.createElement("details");
		details.className =
			"disclosure-has-popup facets__disclosure js-filter" +
			(extraClass ? " " + extraClass : "");
		details.dataset.index = String(index + 1);
		details.open = true;

		const summary = document.createElement("summary");
		summary.className = "facets__summary focus-inset";
		const inner = document.createElement("div");
		inner.className = "facets__summary-inner";
		const title = document.createElement("span");
		title.className = "subtitle facets__summary-title";
		title.textContent = filter.label || filter.name || "";
		if (readout) {
			const count = document.createElement("span");
			count.className = "facets__summary-count";
			count.textContent = readout;
			title.appendChild(count);
		}
		inner.appendChild(title);
		summary.appendChild(inner);
		details.appendChild(summary);

		const display = document.createElement("div");
		display.className = "facets__display";
		details.appendChild(display);

		return { details, display };
	}

	/* Productive #205 (Tagalys review, 16 Sep 2026): the Material / Colour / Height
	   filters are built from Billini's `prefix|value` tags and Tagalys returned the raw
	   tag as the display label - "fabric|synthetic-leather", plus a bare "fabric|" for
	   products whose tag has no value. Display is the theme's job, so: drop the prefix
	   up to the first pipe, read hyphens as spaces and title-case the words. Labels
	   without a pipe (Size "8", Product type "Boots", Tagalys-configured display names)
	   pass through untouched, so this is a no-op once Tagalys cleans them at source.
	   Returns "" for a value-less tag; the caller drops those items. The filter ids
	   the connector needs are untouched - only what the shopper reads changes. */
	function facetValueLabel(raw) {
		const label = raw == null ? "" : String(raw);
		if (label.indexOf("|") === -1) return label;
		const value = label.slice(label.indexOf("|") + 1).trim();
		if (!value) return "";
		/* Multi-value tags ("colour|beige,tortoiseshell") read as a list. */
		return value
			.replace(/[-_]+/g, " ")
			.replace(/\s*,\s*/g, ", ")
			.replace(/\s+/g, " ")
			.replace(/\b([a-z])/g, (m, c) => c.toUpperCase());
	}

	function buildCheckboxFacet(filter, index) {
		let items = (filter.items || filter.values || [])
			.map((item) => Object.assign({}, item, { label: facetValueLabel(item.label) }))
			.filter((item) => item.label !== "");
		if (!items.length) return null;

		/* Tagalys returns values in count order (Size came back 5, 7, 8, 9, 6, 10). When
		   every value is a number, order them numerically; anything else keeps the order
		   the Tagalys admin's "sort order of filter tags" setting produces. */
		const allNumeric = items.every((item) => /^\s*\d+(\.\d+)?\s*$/.test(String(item.label)));
		if (allNumeric) items = items.slice().sort((a, b) => Number(a.label) - Number(b.label));

		const selectedCount = items.filter((item) => item.selected).length;
		const { details, display } = buildDisclosure(
			filter,
			index,
			"",
			selectedCount ? "(" + selectedCount + ")" : "",
		);

		const list = document.createElement("ul");
		list.className = "facets__list list-unstyled";
		list.setAttribute("role", "list");

		items.forEach((item, itemIndex) => {
			const disabled = item.count === 0 && !item.selected;
			const li = document.createElement("li");
			li.className =
				"list-menu__item facets__item" + (disabled ? " facets__item--disabled" : "");

			const id = "TagalysFilter-" + index + "-" + itemIndex;
			const input = document.createElement("input");
			input.type = "checkbox";
			input.className = "focus-inset";
			input.id = id;
			input.checked = !!item.selected;
			input.disabled = disabled;
			/* The filter + item ids the connector's applyFilter/clearFilter expect. */
			input.dataset.filterId = filter.id;
			input.dataset.itemId = item.id;

			const label = document.createElement("label");
			label.setAttribute("for", id);
			label.className =
				"facet-checkbox link-hover-line-outer secondary_text" +
				(disabled ? " facet-checkbox--disabled" : "");

			/* Same inline checkmark the Liquid draws, so the two surfaces match. */
			label.insertAdjacentHTML(
				"afterbegin",
				'<svg class="icon icon-checkmark" width="8" height="7" viewBox="0 0 8 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M1 3.5L3 5.5L7 1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
			);
			const text = document.createElement("span");
			text.className = "facet-checkbox__label";
			text.textContent = item.label;
			label.appendChild(text);
			/* Clear affordance on a selected option (see facets.liquid) - CSS shows it
			   only while the input is checked. */
			const remove = document.createElement("span");
			remove.className = "facet-checkbox__remove";
			remove.setAttribute("aria-hidden", "true");
			label.appendChild(remove);

			li.appendChild(input);
			li.appendChild(label);
			list.appendChild(li);
		});

		display.appendChild(list);
		return details;
	}

	/* Tagalys range values are plain numbers. Shopify's are cents, which the Liquid
	   divided by 100 and ceil'd - that conversion must NOT be carried over here. */
	function buildRangeFacet(filter, index) {
		const min = Number(filter.min);
		const max = Number(filter.max);
		if (!isFinite(min) || !isFinite(max) || max <= 0) return null;

		const selectedMin = Number(
			filter.selected_min != null ? filter.selected_min : min,
		);
		const selectedMax = Number(
			filter.selected_max != null ? filter.selected_max : max,
		);
		const symbol = (strings.settings && strings.settings.currencySymbol) || "";
		const rangeApplied = filter.selected_min != null || filter.selected_max != null;
		const { details, display } = buildDisclosure(
			filter,
			index,
			"facets__disclosure-price",
			rangeApplied
				? "(" + symbol + Math.ceil(selectedMin) + " - " + symbol + Math.ceil(selectedMax) + ")"
				: "",
		);

		const range = document.createElement("price-range");
		range.className = "facets__price";
		range.dataset.filterId = filter.id;
		range.dataset.rangeMin = String(min);
		range.dataset.rangeMax = String(max);

		const numberPair = document.createElement("div");
		numberPair.className = "facets__price-wrapper";
		numberPair.appendChild(
			buildRangeInput("number", "GTE", selectedMin, min, max, index),
		);
		numberPair.appendChild(
			buildRangeInput("number", "LTE", selectedMax, min, max, index),
		);
		range.appendChild(numberPair);

		const slider = document.createElement("div");
		slider.className = "facets__range";
		const span = max - min || 1;
		slider.style.setProperty(
			"--range-min",
			(((selectedMin - min) / span) * 100).toFixed(2) + "%",
		);
		slider.style.setProperty(
			"--range-max",
			(((selectedMax - min) / span) * 100).toFixed(2) + "%",
		);
		slider.appendChild(buildRangeInput("range", "Min", selectedMin, min, max, index));
		slider.appendChild(buildRangeInput("range", "Max", selectedMax, min, max, index));
		range.appendChild(slider);

		display.appendChild(range);
		return details;
	}

	function buildRangeInput(kind, suffix, value, min, max, index) {
		const id = "TagalysRange-" + index + "-" + suffix;

		if (kind === "range") {
			const input = document.createElement("input");
			input.type = "range";
			input.className = "field__range";
			input.id = id;
			input.min = String(min);
			input.max = String(max);
			input.step = "1";
			input.value = String(value);
			input.dataset.rangeBound = suffix.toLowerCase();
			return input;
		}

		const field = document.createElement("div");
		field.className = "field";
		const currency = document.createElement("span");
		currency.className = "field__currency";
		currency.textContent = (strings.settings && strings.settings.currencySymbol) || "";
		const input = document.createElement("input");
		input.type = "number";
		input.className = "field__input";
		input.id = id;
		input.min = String(min);
		input.max = String(max);
		input.step = "1";
		input.value = String(Math.round(value));
		input.dataset.rangeBound = suffix === "GTE" ? "min" : "max";
		field.appendChild(currency);
		field.appendChild(input);
		return field;
	}

	/* Hide the Filter trigger while the response carries no facets, so the toolbar
	   never offers a control that would open an empty panel (the live state on this
	   store today). Count + sort + grid-size stay mounted regardless. */
	function renderFilterTrigger(filters) {
		const trigger = root.querySelector(".facets__button-show");
		const container = root.querySelector(".facets__container");
		const hasFilters = !!(filters && filters.length);
		/* `.hidden` (base.css, `display: none !important`) rather than the `hidden`
		   ATTRIBUTE: .facets__button-show declares `display: flex`, which overrides the
		   attribute's default UA styling, so the button stayed on screen. */
		if (trigger) trigger.classList.toggle("hidden", !hasFilters);
		if (container && !hasFilters) container.classList.remove("active");
	}

	function renderClearAll(filters) {
		const holder = root.querySelector("[data-tagalys-clear-all]");
		if (!holder) return;
		const active = (filters || []).some((filter) =>
			filter.type === "range"
				? filter.selected_min != null || filter.selected_max != null
				: (filter.items || []).some((item) => item.selected),
		);
		/* The state classes belong on the inner .facet-remove (that is where facets.liquid
		   puts them and where component-facets.css reads `facet-remove--disabled` -
		   opacity .3 + pointer-events none). Toggling the outer holder left the
		   server-rendered disabled state in place for good (post-merge QA part 2). */
		const target = holder.querySelector(".facet-remove") || holder;
		target.classList.toggle("active", active);
		target.classList.toggle("facet-remove--disabled", !active);
	}

	/* --------------------------------------------------------------------- sort */

	/* Sort options come from the response as { id, label, selected }. Tagalys defaults
	   to `trending` where Shopify defaulted to `relevance`, so the checked option is
	   whichever Tagalys marks selected - never hardcoded. The markup mirrors
	   snippets/facets-sort.liquid's single `sort_by` radio group so <sort-menu> can
	   reparent it between the desktop popover and the mobile drawer unchanged. */
	function renderSortOptions(options) {
		const list = root.querySelector("[data-sort-list]");
		if (!list || !options) return;

		list.innerHTML = "";
		options.forEach((option, index) => {
			/* Tagalys uses `id` + `label`; the Liquid used Shopify's `value` + `name`. */
			const value = option.id != null ? option.id : option.value;
			const label = option.label != null ? option.label : option.name;
			const id = "SortOption-" + (slugify(value) || index);

			const li = document.createElement("li");
			li.className = "facets-sort__option";

			/* Mirrors snippets/radio.liquid exactly: a visually-hidden input kept
			   ADJACENT to its label, so base.css's `:focus-visible + label` ring and the
			   `:checked + label::before` mark both still apply. */
			const radio = document.createElement("div");
			radio.className = "radio facets-sort__radio";

			const input = document.createElement("input");
			input.className = "radio__input";
			input.type = "radio";
			input.id = id;
			input.name = "sort_by";
			input.value = value;
			input.checked = !!option.selected;

			const labelEl = document.createElement("label");
			labelEl.className = "radio__label";
			labelEl.setAttribute("for", id);
			labelEl.textContent = label;

			radio.appendChild(input);
			radio.appendChild(labelEl);
			li.appendChild(radio);
			list.appendChild(li);

			/* Seed the trigger's value on first paint. <sort-menu> mirrors it on every
			   subsequent selection itself (its own `change` handler), so this only has to
			   cover the initial render. */
			if (option.selected) {
				root.querySelectorAll("[data-sort-value]").forEach((el) => {
					el.textContent = label;
				});
			}
		});
	}

	/* --------------------------------------------------------------- pagination */

	/* NUMBERED PAGES ONLY. `settings.pagination_type` load_more / infinite_scroll are
	   driven by Liquid's `paginate.next.url`, which client rendering removes, so those
	   two modes stay inert on search rather than silently doing nothing.

	   Markup mirrors snippets/pagination.liquid exactly (.pagination-wrapper > nav >
	   ul.pagination__list > li) so component styling applies unchanged. The prev/next
	   carets are CLONED from <template>s the section renders via the icon-caret-left /
	   icon-caret-right SNIPPETS - they are snippets rather than assets, so there is no
	   `inline_asset_content` path for them and SVG must never be hand-written here. */
	function renderPagination(response) {
		if (usesLoadMore) {
			renderLoadMore(response);
			return;
		}
		const holder = root.querySelector("[data-tagalys-pagination]");
		if (!holder) return;

		const totalPages = Number(response.total_pages) || 0;
		const current = Number(response.page) || 1;

		holder.innerHTML = "";
		if (totalPages <= 1) return;

		const nav = document.createElement("nav");
		nav.className = "pagination";
		nav.setAttribute("role", "navigation");
		nav.setAttribute("aria-label", a11yStrings.paginationLabel || "");

		const list = document.createElement("ul");
		list.className = "pagination__list list-unstyled";
		list.setAttribute("role", "list");

		const pageLabel = (page) =>
			(a11yStrings.pageTemplate || "").replace("[number]", page);

		/* Prev / next arrows: a cloned caret inside a button. */
		const addArrow = (page, direction) => {
			const template = root.querySelector(
				'[data-tagalys-caret="' + direction + '"]',
			);
			const li = document.createElement("li");
			const button = document.createElement("button");
			button.type = "button";
			button.className =
				"pagination__item--" +
				(direction === "left" ? "prev" : "next") +
				" pagination__item-arrow link motion-reduce button--unstyled";
			button.dataset.page = String(page);
			button.setAttribute(
				"aria-label",
				(direction === "left" ? a11yStrings.previous : a11yStrings.next) || "",
			);
			if (template) button.appendChild(template.content.cloneNode(true));
			li.appendChild(button);
			list.appendChild(li);
		};

		const addPage = (page, label, isCurrent) => {
			const li = document.createElement("li");
			if (isCurrent) {
				const span = document.createElement("span");
				span.className = "pagination__item pagination__item--current";
				span.setAttribute("aria-current", "page");
				span.setAttribute("aria-label", pageLabel(page));
				span.textContent = label;
				li.appendChild(span);
			} else {
				const button = document.createElement("button");
				button.type = "button";
				button.className = "pagination__item link button--unstyled";
				button.dataset.page = String(page);
				button.setAttribute("aria-label", pageLabel(page));
				const inner = document.createElement("span");
				inner.title = label;
				inner.textContent = label;
				button.appendChild(inner);
				li.appendChild(button);
			}
			list.appendChild(li);
		};

		const addGap = () => {
			const li = document.createElement("li");
			const span = document.createElement("span");
			span.className = "pagination__item";
			span.textContent = "\u2026";
			li.appendChild(span);
			list.appendChild(li);
		};

		if (current > 1) addArrow(current - 1, "left");

		/* Elided window - first, last and the pages either side of the current one, so
		   669 results over 24 per page (28 pages) stays a usable control. */
		const shown = new Set([1, totalPages, current]);
		if (current - 1 > 1) shown.add(current - 1);
		if (current + 1 < totalPages) shown.add(current + 1);

		let previous = 0;
		Array.from(shown)
			.sort((a, b) => a - b)
			.forEach((page) => {
				if (previous && page - previous > 1) addGap();
				addPage(page, String(page), page === current);
				previous = page;
			});

		if (current < totalPages) addArrow(current + 1, "right");

		nav.appendChild(list);
		const wrapper = document.createElement("div");
		wrapper.className = "pagination-wrapper";
		wrapper.appendChild(nav);
		holder.appendChild(wrapper);
	}

	/* #304 - the PLP's load-more control (snippets/load-more.liquid, QA #226): the
	   "Showing X of Y products" count, the page row with the loaded-up-to page
	   underlined, and the Load More text link. Same classes so the snippet's
	   stylesheet applies; the action (button snippet + spinner) is cloned from the
	   <template> the section renders. Page-row numbers jump straight to a page
	   (replace), Load More appends the next page. */
	function renderLoadMore(response) {
		const holder = root.querySelector("[data-tagalys-pagination]");
		if (!holder) return;

		const totalPages = Number(response.total_pages) || 0;
		const current = Number(response.page) || 1;
		const total = Number(response.total_count) || 0;

		holder.innerHTML = "";
		if (total <= 0) return;

		const wrapper = document.createElement("div");
		wrapper.className = "pagination-more no-js-hidden";

		if (loadMoreStrings.showing) {
			const count = document.createElement("p");
			count.className = "pagination-more__count";
			count.textContent = loadMoreStrings.showing
				.replace("[shown]", Math.min(current * PER_PAGE, total))
				.replace("[total]", total);
			wrapper.appendChild(count);
		}

		if (totalPages > 1) {
			const nav = document.createElement("nav");
			nav.className = "pagination-more__pages";
			nav.setAttribute("aria-label", a11yStrings.paginationLabel || "");
			const pageLabel = (page) =>
				(a11yStrings.pageTemplate || "").replace("[number]", page);

			/* Same elided window Liquid's paginate.parts produces. */
			const shown = new Set([1, totalPages, current]);
			if (current - 1 > 1) shown.add(current - 1);
			if (current + 1 < totalPages) shown.add(current + 1);
			let previous = 0;
			Array.from(shown)
				.sort((a, b) => a - b)
				.forEach((page) => {
					if (previous && page - previous > 1) {
						const gap = document.createElement("span");
						gap.className = "pagination-more__page pagination-more__page--gap";
						gap.setAttribute("aria-hidden", "true");
						gap.textContent = "\u2026";
						nav.appendChild(gap);
					}
					if (page === current) {
						const span = document.createElement("span");
						span.className = "pagination-more__page pagination-more__page--current";
						span.setAttribute("aria-current", "page");
						span.setAttribute("aria-label", pageLabel(page));
						span.textContent = String(page);
						nav.appendChild(span);
					} else {
						const button = document.createElement("button");
						button.type = "button";
						button.className = "pagination-more__page button--unstyled";
						button.dataset.page = String(page);
						button.setAttribute("aria-label", pageLabel(page));
						button.textContent = String(page);
						nav.appendChild(button);
					}
					previous = page;
				});
			wrapper.appendChild(nav);
		}

		if (current < totalPages) {
			const template = root.querySelector("[data-tagalys-load-more]");
			if (template) {
				const action = document.createElement("div");
				action.className = "pagination-more__action";
				action.appendChild(template.content.cloneNode(true));
				wrapper.appendChild(action);
			}
		}

		holder.appendChild(wrapper);
	}

	/* ------------------------------------------------------------------- render */

	let connector = null;

	function render(response) {
		/* Tagalys can answer a query with a merchandised redirect. */
		if (response && response.redirect_url) {
			window.location.assign(response.redirect_url);
			return;
		}

		const helpers = connector.helpers;
		const products = response.products || [];
		const filters = response.filters || [];
		const hasSelectedFilters = filters.some((filter) =>
			filter.type === "range"
				? filter.selected_min != null || filter.selected_max != null
				: (filter.items || []).some((item) => item.selected),
		);

		renderCount(
			typeof response.total_count === "number"
				? response.total_count
				: products.length,
		);
		renderFacets(filters);
		renderSortOptions(response.sort_options);
		tagalysReady = true;

		if (isCollection) {
			/* Native-first: the Liquid grid stays until the first refinement. The
			   toolbar above is all this response feeds. */
			if (nativeGrid) {
				setBusy(false);
				return;
			}
			renderGrid(products, response.page);
			renderPagination(response);
			renderEmptyState(hasSelectedFilters, products.length === 0);
			syncUrl(helpers);
			setBusy(false);
			return;
		}

		root.classList.toggle("template-search--empty", products.length === 0);
		/* True no-results (the query itself missed, nothing filtered away): the design
		   (3822:69664) draws only the Collection Header + recommendation sections, so
		   template-search.css hides the Filter Bar and #ProductGridContainer under this
		   class. Filtered-to-zero deliberately does NOT set it — the rail must stay
		   usable so the shopper can relax the filter that stranded them. */
		root.classList.toggle(
			"template-search--no-results",
			products.length === 0 && !hasSelectedFilters,
		);

		renderNoResultsHero(products.length === 0 && !hasSelectedFilters);
		renderQueryVerdict(helpers, products.length === 0 && !hasSelectedFilters);
		renderGrid(products);
		renderPagination(response);
		renderEmptyState(hasSelectedFilters, products.length === 0);

		/* Shareable, reloadable URL for query + filters + sort + page. */
		syncUrl(helpers);
		setBusy(false);
	}

	function renderError() {
		setBusy(false);
		if (isCollection) {
			/* Tagalys refused or failed (the 'collections' feature is account-gated).
			   Nothing to switch: with no successful response the toolbar is still the
			   Liquid one and its controls already route to nativeReload (see the change
			   handler). A refinement that fails mid-session just leaves the last grid
			   standing, with a pending append dropped. */
			appendNext = false;
			return;
		}
		/* The heading's count slot is a placeholder until a response lands
		   (main-search.liquid); a failed request must not leave it pulsing. Fall
		   back to the Shopify count the seed carries, which also resolves the
		   ellipsis in the tab title and breadcrumb. */
		const heading = document.querySelector("[data-tagalys-results-heading]");
		if (heading && heading.hasAttribute("aria-busy")) {
			const seed = Number(heading.getAttribute("data-tagalys-seed-count"));
			renderCount(Number.isFinite(seed) ? seed : 0);
		}
		root.classList.add("template-search--error");
		const empty = root.querySelector("[data-tagalys-empty-message]");
		if (empty && searchStrings.noResultsBody) {
			empty.textContent = searchStrings.noResultsBody;
		}
	}

	/* Shareable, reloadable URL.

	   `getEncodedQueryString` returns a BARE query string (no leading `?`) built from the
	   parameter names configured in snippets/tagalys-bootstrap.liquid - `q`, `filter`,
	   `sort`, `page`. It therefore already carries the search term and the page, so
	   nothing is re-added here: doing so produced `?q=heel&q=heel&page=1`.

	   `page` is omitted by the SDK on page 1, which is what we want - a canonical
	   first-page URL with no redundant `page=1`. */
	function syncUrl(helpers) {
		if (typeof helpers.getEncodedQueryString !== "function") return;

		const encoded = (
			helpers.getEncodedQueryString(helpers.getRequestParams()) || ""
		)
			/* The Collection connector's request params carry the collection id; the
			   path already identifies the collection, so keep it out of the URL. */
			.replace(/(^|&)collection_?[iI]d=[^&]*/g, "")
			.replace(/^[?&]+/, "");

		const next = window.location.pathname + (encoded ? "?" + encoded : "");
		if (next !== window.location.pathname + window.location.search) {
			history.pushState({ tagalys: true }, "", next);
		}
	}

	/* #304 failover: serialise the Liquid-rendered controls the way facets.js's
	   form submit would - every checked Shopify filter (`filter.v.availability`,
	   ...), the price bounds (`filter.v.price.gte` / `.lte`) and the chosen
	   `sort_by` - and reload. Liquid renders the refined page natively. */
	function nativeReload() {
		const params = new URLSearchParams();
		root
			.querySelectorAll("[data-tagalys-facets] input[type='checkbox']:checked")
			.forEach((input) => {
				if (input.name) params.append(input.name, input.value);
			});
		root
			.querySelectorAll("[data-tagalys-facets] input[type='number']")
			.forEach((input) => {
				if (input.name && input.value !== "") params.append(input.name, input.value);
			});
		const sort = root.querySelector("input[name='sort_by']:checked");
		if (sort && sort.value) params.set("sort_by", sort.value);
		const query = params.toString();
		window.location.assign(window.location.pathname + (query ? "?" + query : ""));
	}

	/* ------------------------------------------------------------------ wiring */

	/* One delegated listener per interaction class, bound once to the section root, so
	   re-rendered controls need no re-binding. */
	/* Filter drawer open / close (post-merge QA part 2). The search template loads THIS
	   file instead of facets.js, and the drawer toggle lived only in facets.js's
	   FacetFiltersForm - so once Tagalys started returning filters the Filter button
	   showed but opened nothing. Same contract as the native handler: `.active` on
	   .facets__container drives visibility, body `overflow-hidden` locks scroll, and
	   `filter-open` lingers 700ms for the slide-out transition. */
	function setDrawer(open) {
		const container = root.querySelector(".facets__container");
		if (!container) return;
		container.style.display = "flex";
		container.classList.toggle("active", open);
		document.body.classList.toggle("overflow-hidden", open);
		if (open) {
			document.body.classList.add("filter-open");
		} else {
			setTimeout(() => document.body.classList.remove("filter-open"), 700);
		}
		const trigger = root.querySelector(".facets__button-show");
		if (trigger) {
			const hide = trigger.querySelector(".label-hide");
			const show = trigger.querySelector(".label-show");
			if (hide) hide.classList.toggle("hidden", open);
			if (show) show.classList.toggle("hidden", !open);
		}
	}

	function isDrawerOpen() {
		const container = root.querySelector(".facets__container");
		return !!container && container.classList.contains("active");
	}

	/* Price slider <-> number field sync. facets.js defines <price-range> for this;
	   without it the thumbs moved nothing. Keeps the two thumbs ordered, mirrors them
	   into the number fields the change handler reads, and repaints the track fill. */
	function syncRange(thumb) {
		const range = thumb.closest("price-range");
		if (!range) return;
		const thumbs = range.querySelectorAll("input[type='range'][data-range-bound]");
		const numbers = range.querySelectorAll("input[type='number'][data-range-bound]");
		if (thumbs.length < 2 || numbers.length < 2) return;
		let lo = Number(thumbs[0].value);
		let hi = Number(thumbs[1].value);
		if (lo > hi) {
			if (thumb === thumbs[0]) lo = hi;
			else hi = lo;
			thumb.value = String(thumb === thumbs[0] ? lo : hi);
		}
		numbers[0].value = String(lo);
		numbers[1].value = String(hi);
		/* The Liquid-rendered <price-range> (PLP failover, #304) carries no data-range-*
		   bounds; its thumbs do. */
		const min = Number(range.dataset.rangeMin != null ? range.dataset.rangeMin : thumbs[0].min);
		const max = Number(range.dataset.rangeMax != null ? range.dataset.rangeMax : thumbs[1].max);
		const span = max - min || 1;
		const track = range.querySelector(".facets__range");
		if (track) {
			track.style.setProperty("--range-min", (((lo - min) / span) * 100).toFixed(2) + "%");
			track.style.setProperty("--range-max", (((hi - min) / span) * 100).toFixed(2) + "%");
		}
	}

	function bindEvents() {
		const helpers = connector.helpers;

		root.addEventListener("input", (event) => {
			const target = event.target;
			if (target.type === "range" && target.dataset.rangeBound) syncRange(target);
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && isDrawerOpen()) setDrawer(false);
		});

		root.addEventListener("change", (event) => {
			const target = event.target;

			/* PLP: a Liquid-rendered control (no Tagalys ids on it) - before the first
			   response or after a refusal - applies natively. Sort options are Liquid's
			   until a response has replaced them. */
			if (isCollection) {
				const inRail = !!target.closest("[data-tagalys-facets]");
				const tagalysRendered =
					target.matches("[data-filter-id]") ||
					!!target.closest("price-range[data-filter-id]");
				if (
					(inRail && !tagalysRendered) ||
					(target.name === "sort_by" && !tagalysReady)
				) {
					nativeReload();
					return;
				}
			}

			if (target.matches("input[type='checkbox'][data-filter-id]")) {
				refine();
				const filterId = target.dataset.filterId;
				const selected = Array.from(
					root.querySelectorAll(
						"input[type='checkbox'][data-filter-id='" + filterId + "']:checked",
					),
				).map((input) => input.dataset.itemId);
				if (selected.length) {
					helpers.applyFilter(filterId, selected);
				} else {
					helpers.clearFilter(filterId);
				}
				return;
			}

			if (target.name === "sort_by") {
				refine();
				helpers.applySortOption(target.value);
				return;
			}

			if (target.matches("[data-range-bound]")) {
				const range = target.closest("price-range");
				if (!range || !range.dataset.filterId) return;
				refine();
				const bounds = range.querySelectorAll(
					"input[type='number'][data-range-bound]",
				);
				helpers.applyFilter(range.dataset.filterId, {
					selected_min: Number(bounds[0].value),
					selected_max: Number(bounds[1].value),
				});
			}
		});

		root.addEventListener("click", (event) => {
			if (event.target.closest(".facets__button-show")) {
				event.preventDefault();
				setDrawer(!isDrawerOpen());
				return;
			}
			if (event.target.closest(".facets-modal__close")) {
				event.preventDefault();
				setDrawer(false);
				return;
			}
			/* Scrim: a click on the container itself, outside the sliding wrapper. */
			if (event.target.classList.contains("facets__container")) {
				setDrawer(false);
				return;
			}

			/* #304 Load More: append the next page under the current one. The control
			   is re-rendered from the response, so the in-flight state only has to hold
			   until then. */
			const loadMore = event.target.closest("[data-tagalys-pagination] .js-load-more");
			if (loadMore) {
				event.preventDefault();
				takeOver();
				loadMore.setAttribute("disabled", "");
				loadMore.setAttribute("aria-busy", "true");
				loadMore.classList.add("loading");
				appendNext = true;
				setBusy(true);
				helpers.goToNextPage();
				return;
			}

			const page = event.target.closest("[data-page]");
			if (page) {
				event.preventDefault();
				refine();
				helpers.goToPage(Number(page.dataset.page));
				window.scrollTo({ top: 0, behavior: "smooth" });
				return;
			}

			if (event.target.closest("[data-tagalys-clear-all] a, [data-tagalys-clear-all] button")) {
				/* PLP before any response / after a refusal: the Liquid anchor already
				   points at the unfiltered collection URL - let it navigate. */
				if (isCollection && !tagalysReady) return;
				event.preventDefault();
				refine();
				helpers.clearAllFilters();
			}
		});

		/* Back / forward. The SDK reads its state from the query string, so the simplest
		   correct restore is a reload of the popped URL rather than trying to replay
		   connector state by hand. */
		window.addEventListener("popstate", (event) => {
			if (event.state && event.state.tagalys) window.location.reload();
		});
	}

	window.onTagalysReady(function (Tagalys) {
		const query = currentQuery();
		/* Tagalys needs a real query; the no-query landing state stays Liquid-rendered. */
		if (!isCollection && !query) return;

		if (isCollection) {
			captureTiles();
			connector = Tagalys.APIConnector.Collection.new({
				params: { collectionId: collectionId, perPage: PER_PAGE },
				onSuccess: render,
				onFailure: renderError,
			});
		} else {
			setBusy(true);
			connector = Tagalys.APIConnector.SearchResults.new({
				params: { query: query, perPage: PER_PAGE },
				onSuccess: render,
				onFailure: renderError,
			});
		}

		/* Restore state from the URL before the first fetch, so a shared or reloaded link
		   lands on the same filters, sort and page it was copied from. The connector does
		   NOT do this on its own - without it, `?sort=sale_price-asc` was parsed away and
		   the page silently reverted to the default `trending` ordering.

		   `getRequestParamsFromWindowLocation` reads exactly the parameter names configured
		   in the bootstrap (`q`, `filter`, `sort`, `page`), so the two stay in step. */
		const fromUrl = connector.helpers.getRequestParamsFromWindowLocation();
		if (fromUrl && Object.keys(fromUrl).length) {
			connector.helpers.setRequestState(function (state) {
				if (fromUrl.filters) state.filters = fromUrl.filters;
				if (fromUrl.sort) state.sort = fromUrl.sort;
				if (fromUrl.page) state.page = fromUrl.page;
				return state;
			}, false);
		}
		/* A shared / reloaded PLP URL that already carries Tagalys state is a refined
		   view - the Liquid page-1 grid is wrong for it, so the client paints from the
		   first response. */
		if (
			isCollection &&
			fromUrl &&
			((fromUrl.filters && Object.keys(fromUrl.filters).length) ||
				fromUrl.sort ||
				Number(fromUrl.page) > 1)
		) {
			takeOver();
			setBusy(true);
		}

		bindEvents();
		/* Connectors do not fetch until `call()`. */
		connector.helpers.call();
	});
})();
